import type { JSX } from 'react'

import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group'
import { clsx } from 'clsx'

import {
  ChannelIcon,
  Flex,
  Tooltip,
  VisuallyHidden,
} from '@buffer-mono/popcorn'
import type { ChannelType } from '@buffer-mono/popcorn'

import styles from './PreviewChannelSelection.module.css'

export interface PreviewChannelSelectionItem {
  id: string
  service: ChannelType
  label: string
}

export function PreviewChannelSelectionView({
  channels,
  selectedIds,
  onToggle,
  overflow = 'wrap',
}: {
  channels: readonly PreviewChannelSelectionItem[]
  selectedIds: ReadonlySet<string>
  onToggle: (channelId: string) => void
  overflow?: 'wrap' | 'scroll'
}): JSX.Element {
  return (
    <>
      <VisuallyHidden>Select channels</VisuallyHidden>

      <ToggleGroupPrimitive.Root
        type="multiple"
        orientation="horizontal"
        value={Array.from(selectedIds)}
        className={clsx(styles.root, overflow === 'scroll' && styles.scroll)}
      >
        <Flex wrap="wrap" gap="sm" align="center">
          {channels.map((channel) => {
            const isSelected = selectedIds.has(channel.id)

            return (
              <Tooltip key={channel.id} content={channel.label}>
                <ToggleGroupPrimitive.Item
                  value={channel.id}
                  data-channel={channel.service}
                  className={clsx(styles.item, isSelected && styles.selected)}
                  aria-label={`${channel.label}${isSelected ? ' (selected)' : ''}`}
                  onClick={(e) => {
                    e.preventDefault()
                    onToggle(channel.id)
                  }}
                >
                  <ChannelIcon
                    type={channel.service}
                    color={isSelected ? 'inverted' : 'subtle'}
                    size="medium"
                  />
                </ToggleGroupPrimitive.Item>
              </Tooltip>
            )
          })}
        </Flex>
      </ToggleGroupPrimitive.Root>
    </>
  )
}
